import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Package, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Tiered "Pallet-Slab" Shipping editor.
 *
 * Admins define quantity ranges + a flat shipping cost for each range on a
 * per-product basis (e.g. 1-20 units = £50, 21-50 units = £100).
 *
 * NON-DESTRUCTIVE: when the slab table is empty, the product falls back to
 * the existing per-product overrides (free_delivery_qty / individual_delivery_cost)
 * and then to the global £75 free-delivery / £4.95 rule. Nothing changes for
 * legacy products.
 */
const ShippingSlabsEditor = ({ product, onSave }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const initial = Array.isArray(product?.shipping_slabs) ? product.shipping_slabs : [];
    setRows(initial.map(r => ({
      min_qty: Number(r?.min_qty || 0),
      max_qty: Number(r?.max_qty || 0),
      cost: Number(r?.cost || 0),
    })));
  }, [product?.id]);

  const addRow = () => {
    const last = rows[rows.length - 1];
    const nextMin = last ? Math.max(1, Number(last.max_qty || 0) + 1) : 1;
    setRows([...rows, { min_qty: nextMin, max_qty: 0, cost: 0 }]);
  };

  const updateRow = (idx, key, value) => {
    const copy = [...rows];
    copy[idx] = { ...copy[idx], [key]: Number(value) || 0 };
    setRows(copy);
  };

  const removeRow = (idx) => {
    const copy = [...rows];
    copy.splice(idx, 1);
    setRows(copy);
  };

  const save = async () => {
    // Basic validation: min <= max (or max = 0), no negative costs.
    for (const r of rows) {
      if (r.min_qty <= 0) return toast.error('Min qty must be at least 1 for each row');
      if (r.max_qty !== 0 && r.max_qty < r.min_qty) return toast.error('Max qty must be ≥ Min qty (or 0 for "no upper limit")');
      if (r.cost < 0) return toast.error('Cost cannot be negative');
    }
    setBusy(true);
    try {
      await onSave(rows);
      toast.success(rows.length === 0 ? 'Shipping slabs cleared — using default delivery rules' : 'Shipping slabs saved');
    } catch (e) {
      toast.error('Could not save slabs');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-dashed border-neutral-300 pt-3" data-testid={`shipping-slabs-${product.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Package className="w-3.5 h-3.5 text-[#F46A25]" />
          <span className="text-[11px] uppercase font-bold text-[#F46A25]">Tiered pallet-slab shipping</span>
          <span className="text-[10px] text-neutral-500 font-normal normal-case">(empty = use default delivery rules)</span>
        </div>
        <button
          type="button"
          onClick={addRow}
          data-testid={`add-slab-${product.id}`}
          className="text-[11px] font-bold text-[#F46A25] hover:text-[#12263A] inline-flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add slab
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-[11px] text-neutral-500 italic">
          No slabs defined. Delivery falls back to the existing rules.
        </p>
      ) : (
        <div className="space-y-2">
          {/* Column headers — visible only on ≥sm screens; on mobile each row shows its own labels */}
          <div className="hidden sm:grid sm:grid-cols-[1fr_1fr_1fr_28px] gap-2 text-[10px] uppercase font-bold text-neutral-500">
            <span>Min qty</span>
            <span>Max qty <span className="text-neutral-400 normal-case font-normal">(0 = no limit)</span></span>
            <span>Cost (£)</span>
            <span></span>
          </div>
          {rows.map((r, i) => (
            <div
              key={i}
              className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_28px] gap-2 items-end sm:items-center bg-neutral-50 sm:bg-transparent p-2 sm:p-0 rounded-sm border sm:border-none border-neutral-200"
              data-testid={`slab-row-${product.id}-${i}`}
            >
              <label className="block">
                <span className="sm:hidden text-[10px] uppercase font-bold text-neutral-500 block mb-0.5">Min qty</span>
                <input
                  type="number"
                  min="1"
                  value={r.min_qty}
                  onChange={e => updateRow(i, 'min_qty', e.target.value)}
                  data-testid={`slab-min-${product.id}-${i}`}
                  className="w-full border border-neutral-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
                />
              </label>
              <label className="block">
                <span className="sm:hidden text-[10px] uppercase font-bold text-neutral-500 block mb-0.5">Max qty <span className="normal-case font-normal text-neutral-400">(0 = ∞)</span></span>
                <input
                  type="number"
                  min="0"
                  value={r.max_qty}
                  onChange={e => updateRow(i, 'max_qty', e.target.value)}
                  data-testid={`slab-max-${product.id}-${i}`}
                  className="w-full border border-neutral-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
                />
              </label>
              <label className="block">
                <span className="sm:hidden text-[10px] uppercase font-bold text-neutral-500 block mb-0.5">Cost (£)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={r.cost}
                  onChange={e => updateRow(i, 'cost', e.target.value)}
                  data-testid={`slab-cost-${product.id}-${i}`}
                  className="w-full border border-neutral-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
                />
              </label>
              <button
                type="button"
                onClick={() => removeRow(i)}
                aria-label="Remove slab row"
                data-testid={`slab-remove-${product.id}-${i}`}
                className="text-red-500 hover:text-red-700 flex items-center justify-center sm:justify-center col-span-2 sm:col-span-1 border border-red-200 sm:border-none rounded-sm py-1 sm:py-0 gap-1 text-[11px] font-semibold sm:font-normal"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="sm:hidden">Remove</span>
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={save}
        disabled={busy}
        data-testid={`save-slabs-${product.id}`}
        className="mt-2 bg-[#12263A] hover:bg-[#F46A25] text-white text-[11px] font-bold px-3 py-1.5 rounded-sm inline-flex items-center gap-1 disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save slabs'}
      </button>
    </div>
  );
};

export default ShippingSlabsEditor;
